import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import { episodeArcs, episodeStages } from "@/mocks/drama";

const stageStyles: Record<string, string> = {
  start: "bg-secondary-100 text-secondary-900",
  develop: "bg-accent-100 text-accent-900",
  turn: "bg-primary-100 text-primary-700",
  finale: "bg-primary-500 text-background-50",
};

export default function EpisodeGuide() {
  const [stage, setStage] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [openId, setOpenId] = useState<string | null>(episodeArcs[0].id);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return episodeArcs.filter((arc) => {
      const matchStage = stage === "all" || arc.stage === stage;
      const matchKeyword =
        kw === "" ||
        arc.title.includes(kw) ||
        arc.range.includes(kw) ||
        arc.summary.includes(kw) ||
        arc.tags.some((tag) => tag.includes(kw));
      return matchStage && matchKeyword;
    });
  }, [stage, keyword]);

  return (
    <section id="episodes" className="w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 font-label text-sm tracking-[0.3em] text-primary-600">
                <i className="ri-list-check-2" />
                分 集 剧 情
              </p>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                按阶段梳理的全剧脉络
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                全剧按开端、发展、转折、结局四个阶段拆成八段脉络。用筛选或搜索快速定位某一阶段，点击条目可展开详细说明。
              </p>
            </div>

            <div className="w-full lg:w-72">
              <label htmlFor="episode-search" className="sr-only">
                搜索分集剧情
              </label>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  id="episode-search"
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索剧情关键词，如「信任」"
                  className="w-full border-0 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {keyword !== "" && (
                  <button
                    type="button"
                    aria-label="清除搜索"
                    onClick={() => setKeyword("")}
                    className="text-foreground-400 transition hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-line" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={60}>
          <div className="mt-8 inline-flex flex-wrap gap-1 rounded-full border border-background-300 bg-background-50 p-1">
            {episodeStages.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setStage(item.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  stage === item.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 space-y-3">
          {filtered.map((arc, idx) => {
            const isOpen = openId === arc.id;
            return (
              <Reveal key={arc.id} delay={idx * 50}>
                <div className="overflow-hidden rounded-lg border border-background-300 bg-background-50">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : arc.id)}
                    className="flex w-full items-center gap-4 px-4 py-4 text-left transition hover:bg-background-100 md:px-6 md:py-5"
                  >
                    <span className="flex flex-col items-center rounded-md bg-background-100 px-3 py-1.5">
                      <span className="font-heading text-xs font-bold text-foreground-500">
                        {arc.range.replace(/第 | 集/g, "").split("–")[0]}
                      </span>
                    </span>
                    <span className="flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-base font-bold text-foreground-950 md:text-lg">
                          {arc.title}
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${stageStyles[arc.stage]}`}
                        >
                          {arc.stageLabel}
                        </span>
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">{arc.range}</span>
                    </span>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg transition ${
                        isOpen ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                    </span>
                  </button>

                  {isOpen && (
                    <div className="animate-slide-down border-t border-background-200 px-4 py-4 md:px-6 md:py-5">
                      <p className="text-sm leading-loose text-foreground-700 md:text-[15px]">
                        {arc.summary}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {arc.tags.map((tag) => (
                          <span
                            key={tag}
                            className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                          >
                            <i className="ri-price-tag-3-line" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-600">没有找到匹配的剧情段落</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setStage("all");
                }}
                className="mt-4 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}